/**
 * pseudonymize.ts — PII-Pseudonymisierung für LLM-Inputs
 *
 * Pure function, vollständig getestet. Diese Datei IST der Datenschutz-Anker
 * für die Mistral-Integration. Wenn hier etwas durchrutscht, sieht Mistral
 * Klartext-PII. Daher:
 *   1. 95%+ Coverage Pflicht
 *   2. Defense-in-depth: zusätzlich greift im mistralClient.ts ein
 *      PII-Pattern-Re-Check VOR dem API-Call.
 *
 * Regeln:
 *   - Klarname (full + first allein) → "die Mandant:in"
 *   - Adresse → grobe Klassifikation (Großstadt / Mittelstadt / ländliche Region)
 *   - Geburtsdatum → "X Jahre alt" (anhand currentDate)
 *   - Aktenzeichen → "[Aktenzeichen]"
 *   - Jobcenter-Name → "[Jobcenter]"
 *   - E-Mail-Pattern → "[E-Mail]"
 *   - Telefon-Pattern → "[Telefon]"
 *   - IBAN-Pattern → "[IBAN]"
 *   - PLZ + Ort → "[PLZ-Ort]"
 *
 * Was diese Funktion bewusst NICHT tut:
 *   - Branchenspezifische Daten anonymisieren (z.B. "Bäckerei in München") —
 *     branch + business sind FACHLICHE Kontext-Informationen, ohne die das
 *     LLM keine sinnvollen Vorschläge machen kann. Petschwork hat im LV
 *     dokumentiert, dass branche/legalForm Pflicht-Kontext sind.
 *   - BWA-Roh-Daten verschleiern — Zahlen sind kein PII per sich.
 *   - Score-Werte oder Dimension-Texte umschreiben — die SIND der Input.
 *
 * Sprachregel: "die Mandant:in" (Gender-Sternchen mit Doppelpunkt, behörden-
 * konform für SGB-II-Kontext nach BMAS-Empfehlung 2024).
 */

const MANDANT_TOKEN = 'die Mandant:in';
const JOBCENTER_TOKEN = '[Jobcenter]';
const FILE_NUMBER_TOKEN = '[Aktenzeichen]';
const EMAIL_TOKEN = '[E-Mail]';
const PHONE_TOKEN = '[Telefon]';
const IBAN_TOKEN = '[IBAN]';
const PLZ_ORT_TOKEN = '[PLZ-Ort]';

export interface PseudonymizationContext {
  /** Klarname (Vorname + Nachname) der Mandantin/des Mandanten */
  readonly clientFullName: string;
  /** Erster Vorname allein — falls im Text isoliert vorkommt */
  readonly clientFirstName?: string;
  /** Adresse → wird zu Stadt-Klassifikation reduziert */
  readonly clientAddress?: string;
  /** Geburtsdatum → Alter zum currentDate */
  readonly clientBirthDate?: Date;
  /** Jobcenter-Aktenzeichen */
  readonly jcFileNumber?: string;
  /** Jobcenter-Name */
  readonly jcName?: string;
  /** Stichtag für Altersberechnung. Default: today. */
  readonly currentDate?: Date;
}

export interface PseudonymizedInput {
  /** Der pseudonymisierte Text-Body — sicher für Mistral. */
  readonly content: string;
  /**
   * Mapping pseudonym → original. Local-Storage für UI, damit das LLM-Output
   * optional rehydriert werden kann. Niemals an Mistral senden.
   */
  readonly mapping: ReadonlyMap<string, string>;
}

/**
 * Großstädte > 500.000 EW (Stand 2025, Statistisches Bundesamt).
 * Lower-case-Set für O(1)-Lookup.
 */
const GROSSSTADT_SET = new Set([
  'berlin',
  'hamburg',
  'münchen',
  'muenchen',
  'köln',
  'koeln',
  'frankfurt',
  'frankfurt am main',
  'stuttgart',
  'düsseldorf',
  'duesseldorf',
  'leipzig',
  'dortmund',
  'essen',
  'bremen',
  'dresden',
  'hannover',
  'nürnberg',
  'nuernberg',
  'duisburg',
]);

/**
 * Mittelstädte 100.000–500.000 EW (Auswahl der häufigsten — keine
 * abschließende Liste, sondern eine Heuristik).
 */
const MITTELSTADT_SET = new Set([
  'bochum',
  'wuppertal',
  'bielefeld',
  'bonn',
  'münster',
  'muenster',
  'karlsruhe',
  'mannheim',
  'augsburg',
  'wiesbaden',
  'mönchengladbach',
  'moenchengladbach',
  'gelsenkirchen',
  'braunschweig',
  'chemnitz',
  'kiel',
  'aachen',
  'halle',
  'magdeburg',
  'freiburg',
  'krefeld',
  'lübeck',
  'luebeck',
  'oberhausen',
  'erfurt',
  'mainz',
  'rostock',
  'kassel',
  'hagen',
  'potsdam',
  'saarbrücken',
  'saarbruecken',
  'hamm',
  'mülheim',
  'muelheim',
  'ludwigshafen',
  'leverkusen',
  'oldenburg',
  'osnabrück',
  'osnabrueck',
  'solingen',
  'heidelberg',
  'darmstadt',
  'regensburg',
  'paderborn',
  'ingolstadt',
  'würzburg',
  'wuerzburg',
  'wolfsburg',
  'fürth',
  'fuerth',
  'ulm',
  'heilbronn',
  'pforzheim',
  'göttingen',
  'goettingen',
  'bottrop',
  'trier',
  'recklinghausen',
  'reutlingen',
  'bremerhaven',
  'koblenz',
  'bergisch gladbach',
  'jena',
  'remscheid',
  'erlangen',
  'moers',
  'siegen',
  'hildesheim',
  'salzgitter',
  'kaiserslautern',
  'gütersloh',
  'guetersloh',
  'cottbus',
]);

/**
 * Escape user-supplied substring for safe RegExp insertion.
 */
function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Berechnet das Alter zum Stichtag (ganze Jahre, abgerundet).
 */
function ageAtDate(birth: Date, ref: Date): number {
  let age = ref.getFullYear() - birth.getFullYear();
  const m = ref.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < birth.getDate())) {
    age--;
  }
  return Math.max(0, age);
}

/**
 * Reduziert eine Adresse auf Stadt-Klassifikation.
 *   "Müllerstraße 12, 80331 München" → "[Großstadt]"
 *   "Hauptstr. 4, 17033 Neubrandenburg" → "[Mittelstadt]"
 *   "Am Wald 7, 17321 Cantnitz" → "[ländliche Region]"
 *
 * Heuristik: extrahiert PLZ + Stadtname, schlägt in den Sets nach.
 * Wenn keine PLZ erkennbar → "[ländliche Region]" (konservativ).
 *
 * Exportiert für Testbarkeit; nicht für Aufrufer aus dem Frontend gedacht.
 */
export function classifyAddress(address: string): string {
  const trimmed = address.trim();
  if (trimmed.length === 0) return '[ländliche Region]';

  // Suche PLZ (5 Ziffern) + Stadtname
  const match = /\b(\d{5})\s+([A-ZÄÖÜ][\wÄÖÜäöüß -]+?)(?:[,;]|$)/u.exec(trimmed);
  if (!match) {
    // Versuche reinen Stadtnamen ohne PLZ zu erkennen
    const lower = trimmed.toLowerCase();
    for (const city of GROSSSTADT_SET) {
      if (lower.includes(city)) return '[Großstadt]';
    }
    for (const city of MITTELSTADT_SET) {
      if (lower.includes(city)) return '[Mittelstadt]';
    }
    return '[ländliche Region]';
  }

  const cityRaw = match[2]?.trim().toLowerCase() ?? '';
  if (cityRaw.length === 0) return '[ländliche Region]';
  if (GROSSSTADT_SET.has(cityRaw)) return '[Großstadt]';
  if (MITTELSTADT_SET.has(cityRaw)) return '[Mittelstadt]';
  return '[ländliche Region]';
}

/**
 * Pseudonymisiert einen Text-Body. Reihenfolge der Regeln ist wichtig:
 *   1. Längere Patterns zuerst (E-Mail vor Telefon — Telefon-Pattern matched
 *      sonst Teile der E-Mail).
 *   2. Klarname vor Vorname (Vorname ist Substring des Vollnamens).
 *   3. Adresse vor PLZ-Ort (sonst doppelte Maskierung).
 *
 * @returns content (pseudonymisierter Text) + mapping (rehydrate-Tabelle).
 */
export function pseudonymize(text: string, ctx: PseudonymizationContext): PseudonymizedInput {
  if (typeof text !== 'string') {
    throw new Error('pseudonymize: text muss ein String sein');
  }
  const mapping = new Map<string, string>();
  let out = text;

  // 1. E-Mail-Pattern (vor Telefon, da @user.de überlappt)
  out = out.replace(/\b[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}\b/g, (m) => {
    mapping.set(EMAIL_TOKEN, m);
    return EMAIL_TOKEN;
  });

  // 2. IBAN (DE + ggf. internationale 2-Buchstaben-Codes, hier nur DE)
  out = out.replace(/\bDE\d{20}\b/g, (m) => {
    mapping.set(IBAN_TOKEN, m);
    return IBAN_TOKEN;
  });

  // 3. Jobcenter-Aktenzeichen (Format meist: 3-4 Ziffern / 10 Ziffern, z.B.
  //    "12345/0123456789" oder "1234A/12345" — wir nehmen jc-fileNumber aus
  //    dem Kontext wörtlich)
  if (ctx.jcFileNumber && ctx.jcFileNumber.trim().length > 0) {
    const fn = escapeRegExp(ctx.jcFileNumber.trim());
    out = out.replace(new RegExp(fn, 'gi'), () => {
      mapping.set(FILE_NUMBER_TOKEN, ctx.jcFileNumber ?? '');
      return FILE_NUMBER_TOKEN;
    });
  }

  // 4. Jobcenter-Name (whitespace-tolerant — Berater-Freitext hat oft
  //    Zeilenumbrüche oder doppelte Spaces zwischen den Tokens)
  if (ctx.jcName && ctx.jcName.trim().length > 0) {
    const tokens = ctx.jcName.trim().split(/\s+/).map(escapeRegExp);
    const jnPattern = tokens.join('\\s+');
    out = out.replace(new RegExp(jnPattern, 'gi'), () => {
      mapping.set(JOBCENTER_TOKEN, ctx.jcName ?? '');
      return JOBCENTER_TOKEN;
    });
  }

  // 5. Volle Adresse → Klassifikation
  if (ctx.clientAddress && ctx.clientAddress.trim().length > 0) {
    const addrClass = classifyAddress(ctx.clientAddress);
    const addr = escapeRegExp(ctx.clientAddress.trim());
    out = out.replace(new RegExp(addr, 'gi'), () => {
      mapping.set(addrClass, ctx.clientAddress ?? '');
      return addrClass;
    });
  }

  // 6. Telefon-Pattern: +49 ... oder 0XX ... — nach E-Mail, da Telefon-
  //    Pattern in E-Mails überlappen kann.
  out = out.replace(/\+49[\s/()-]?\d[\d\s/()-]{6,}\d/g, (m) => {
    mapping.set(PHONE_TOKEN, m);
    return PHONE_TOKEN;
  });
  out = out.replace(/\b0\d{2,4}[\s/-]\d[\d\s/-]{4,}\d\b/g, (m) => {
    mapping.set(PHONE_TOKEN, m);
    return PHONE_TOKEN;
  });

  // 7. Klarname (full)
  const fullName = ctx.clientFullName.trim();
  if (fullName.length > 0) {
    const escaped = escapeRegExp(fullName);
    out = out.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), () => {
      mapping.set(MANDANT_TOKEN, fullName);
      return MANDANT_TOKEN;
    });
  }

  // 8. Klarname (last name allein, wenn fullName 2+ Tokens hat) —
  //    Bei "Max Mustermann" matchen wir auch isoliertes "Mustermann".
  const nameParts = fullName.split(/\s+/).filter((p) => p.length > 0);
  if (nameParts.length >= 2) {
    // Nachname = letztes Token. Adelsprädikate / Doppelnamen werden hier
    // bewusst nicht spezialbehandelt — false-negatives sind sicherer als
    // false-positives ("von" matchen würde zu viel).
    const lastName = nameParts[nameParts.length - 1] ?? '';
    if (lastName.length >= 3) {
      const escaped = escapeRegExp(lastName);
      out = out.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), () => {
        mapping.set(MANDANT_TOKEN, fullName);
        return MANDANT_TOKEN;
      });
    }
  }

  // 9. Vorname allein (falls clientFirstName gesetzt und ≥3 Zeichen — kürzere
  //    Vornamen wie "Li" produzieren zu viele false-positives)
  const firstName = ctx.clientFirstName?.trim();
  if (firstName && firstName.length >= 3) {
    const escaped = escapeRegExp(firstName);
    out = out.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), () => {
      mapping.set(MANDANT_TOKEN, firstName);
      return MANDANT_TOKEN;
    });
  }

  // 10. Geburtsdatum → "X Jahre alt"
  if (ctx.clientBirthDate) {
    const refDate = ctx.currentDate ?? new Date();
    const age = ageAtDate(ctx.clientBirthDate, refDate);
    const ageLabel = `${String(age)} Jahre alt`;
    // ISO-Format: 1987-04-23
    const iso = ctx.clientBirthDate.toISOString().slice(0, 10);
    const isoEscaped = escapeRegExp(iso);
    out = out.replace(new RegExp(isoEscaped, 'g'), () => {
      mapping.set(ageLabel, iso);
      return ageLabel;
    });
    // DE-Format: 23.04.1987
    const day = String(ctx.clientBirthDate.getDate()).padStart(2, '0');
    const month = String(ctx.clientBirthDate.getMonth() + 1).padStart(2, '0');
    const year = String(ctx.clientBirthDate.getFullYear());
    const deFmt = `${day}.${month}.${year}`;
    const deEscaped = escapeRegExp(deFmt);
    out = out.replace(new RegExp(deEscaped, 'g'), () => {
      mapping.set(ageLabel, deFmt);
      return ageLabel;
    });
  }

  // 11. Residuale PLZ + Ort (5 Ziffern + Stadt) — fängt Reste, die nach
  //     Klar-Adress-Ersetzung übrig bleiben (z.B. wenn im Freitext eine
  //     andere Adresse erwähnt wird).
  out = out.replace(/\b\d{5}\s+[A-ZÄÖÜ][\wÄÖÜäöüß-]+\b/gu, () => {
    mapping.set(PLZ_ORT_TOKEN, '[PLZ-Ort masked]');
    return PLZ_ORT_TOKEN;
  });

  return { content: out, mapping };
}

/**
 * Wendet die Mapping-Tabelle rückwärts an: ersetzt Pseudonyme im LLM-Output
 * wieder durch Originalwerte. Optional — Mistral gibt typischerweise selbst
 * keine Klartext-Werte zurück, aber wenn der Berater den Vorschlag direkt
 * übernimmt, will er „die Mandantin Maja Schmitz" lesen, nicht „die Mandant:in".
 */
export function rehydrate(text: string, mapping: ReadonlyMap<string, string>): string {
  if (mapping.size === 0) return text;
  let out = text;
  for (const [pseudonym, original] of mapping.entries()) {
    if (pseudonym.length === 0) continue;
    out = out.split(pseudonym).join(original);
  }
  return out;
}
